import React from 'react';
import { Task } from '../../types/task';
import { useLanguage } from '../../contexts/LanguageContext';
import { useProjects } from '../../hooks/useProjects';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../common/Modal';
import TimeTracker from './TimeTracker';
import TaskComments from './TaskComments';
import { 
  DocumentTextIcon, 
  ClipboardDocumentListIcon,
  ChartBarIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FireIcon,
  TagIcon,
  BriefcaseIcon,
  UserGroupIcon,
  EyeIcon,
  CurrencyDollarIcon,
  BuildingOffice2Icon,
  ChatBubbleLeftIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

interface TaskViewProps {
  task: Task;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (task: Task) => void;
  onAddComment?: (message: string, files?: File[]) => Promise<void>;
  onDeleteComment?: (commentId: string) => Promise<void>;
  onUpdateComment?: (commentId: string, message: string) => Promise<void>;
  onDeleteAttachment?: (commentId: string, attachmentId: string) => Promise<void>;
  onExportTask?: () => void;
}

const TaskView: React.FC<TaskViewProps> = ({ 
  task, 
  isOpen, 
  onClose, 
  onEdit,
  onAddComment,
  onDeleteComment,
  onUpdateComment,
  onDeleteAttachment,
  onExportTask
}) => {
  const { t } = useLanguage();
  const { projects } = useProjects();

  // Projekt név lekérése
  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Ismeretlen projekt';
  };

  // Dátum formázás
  const formatDate = (date: any) => {
    if (!date) return '-';
    
    try {
      let dateObj: Date;
      if (date instanceof Date) {
        dateObj = date;
      } else if (typeof date === 'object' && date.toDate) {
        dateObj = date.toDate();
      } else {
        dateObj = new Date(date);
      }
      
      if (isNaN(dateObj.getTime())) {
        return 'Érvénytelen dátum';
      }
      
      return dateObj.toLocaleDateString('hu-HU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error, date);
      return 'Dátum hiba';
    }
  };

  // Dátum és idő formázás
  const formatDateTime = (date: any) => {
    if (!date) return '-';
    
    try {
      let dateObj: Date;
      if (date instanceof Date) {
        dateObj = date;
      } else if (typeof date === 'object' && date.toDate) {
        dateObj = date.toDate();
      } else {
        dateObj = new Date(date);
      }
      
      if (isNaN(dateObj.getTime())) {
        return 'Érvénytelen dátum';
      }
      
      return dateObj.toLocaleString('hu-HU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('DateTime formatting error:', error, date);
      return 'Dátum hiba';
    }
  };

  // Státusz információk
  const getStatusInfo = (status: Task['status']) => {
    const isDark = document.documentElement.classList.contains('dark');
    
    switch (status) {
      case 'todo':
        return { 
          label: t('tasks.status.todo') || 'Tennivaló',
          icon: ClockIcon,
          color: isDark ? '#9ca3af' : '#374151',
          bgColor: isDark ? '#374151' : '#f3f4f6'
        };
      case 'in_progress':
        return { 
          label: t('tasks.status.in_progress') || 'Folyamatban',
          icon: ChartBarIcon,
          color: isDark ? '#60a5fa' : '#1d4ed8',
          bgColor: isDark ? '#1e3a8a' : '#dbeafe'
        };
      case 'completed':
        return { 
          label: t('tasks.status.completed') || 'Befejezve',
          icon: CheckCircleIcon,
          color: isDark ? '#34d399' : '#059669',
          bgColor: isDark ? '#065f46' : '#d1fae5'
        };
      case 'cancelled':
        return { 
          label: t('tasks.status.cancelled') || 'Megszakítva',
          icon: ExclamationTriangleIcon,
          color: isDark ? '#f87171' : '#dc2626',
          bgColor: isDark ? '#7f1d1d' : '#fee2e2'
        };
      default:
        return { 
          label: t('tasks.status.todo') || 'Tennivaló',
          icon: ClockIcon,
          color: isDark ? '#9ca3af' : '#374151',
          bgColor: isDark ? '#374151' : '#f3f4f6'
        };
    }
  };

  // Prioritás információk
  const getPriorityInfo = (priority: Task['priority']) => {
    const isDark = document.documentElement.classList.contains('dark');
    
    switch (priority) {
      case 'low':
        return { 
          label: t('tasks.priority.low') || 'Alacsony',
          icon: CheckCircleIcon,
          color: isDark ? '#34d399' : '#059669',
          bgColor: isDark ? '#065f46' : '#d1fae5'
        };
      case 'medium':
        return { 
          label: t('tasks.priority.medium') || 'Közepes',
          icon: ClockIcon,
          color: isDark ? '#fbbf24' : '#d97706',
          bgColor: isDark ? '#92400e' : '#fef3c7'
        };
      case 'high':
        return { 
          label: t('tasks.priority.high') || 'Magas',
          icon: ExclamationTriangleIcon,
          color: isDark ? '#fb923c' : '#ea580c',
          bgColor: isDark ? '#9a3412' : '#fed7aa'
        };
      case 'urgent':
        return { 
          label: t('tasks.priority.urgent') || 'Sürgős',
          icon: FireIcon,
          color: isDark ? '#f87171' : '#dc2626',
          bgColor: isDark ? '#7f1d1d' : '#fee2e2'
        };
      default:
        return { 
          label: t('tasks.priority.low') || 'Alacsony',
          icon: CheckCircleIcon,
          color: isDark ? '#34d399' : '#059669',
          bgColor: isDark ? '#065f46' : '#d1fae5'
        };
    }
  };

  // Határidő státusz
  const dueDateStatus = () => {
    if (!task.dueDate) return 'normal';
    
    const now = new Date();
    const dueDate = new Date(task.dueDate);
    const diffTime = dueDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'overdue';
    if (diffDays <= 1) return 'warning';
    return 'normal';
  };

  const statusInfo = getStatusInfo(task.status);
  const priorityInfo = getPriorityInfo(task.priority);
  const StatusIcon = statusInfo.icon;
  const PriorityIcon = priorityInfo.icon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${task.taskNumber} - ${task.title}`}
      size="5xl"
      zIndex={60}
    >
      <div className="p-2 md:p-4 space-y-2 md:space-y-4">
        
        {/* 🔵 Alapvető információk */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-2 md:p-4 border border-blue-200 dark:border-blue-700">
          <div className="flex items-center mb-2 md:mb-3">
            <div className="bg-blue-500 rounded-full p-1 md:p-1.5 mr-1.5 md:mr-2">
              <ClipboardDocumentListIcon className="h-3 w-3 md:h-4 md:w-4 text-white" />
            </div>
            <h3 className="text-sm md:text-base font-semibold text-blue-900 dark:text-blue-300">
              {t('tasks.sections.basicInfo') || 'Alapvető információk'}
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
            <div>
              <label className="block text-[10px] md:text-xs font-medium text-blue-700 dark:text-blue-300 mb-0.5 md:mb-1">
                {t('tasks.fields.taskNumber') || 'Feladat szám'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 font-medium text-xs md:text-sm">{task.taskNumber}</div>
            </div>

            <div>
              <label className="block text-[10px] md:text-xs font-medium text-blue-700 dark:text-blue-300 mb-0.5 md:mb-1">
                {t('tasks.fields.title') || 'Cím'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 font-medium text-xs md:text-sm">{task.title}</div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-[10px] md:text-xs font-medium text-blue-700 dark:text-blue-300 mb-0.5 md:mb-1">
                {t('tasks.fields.description') || 'Leírás'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 text-xs md:text-sm whitespace-pre-wrap break-words bg-blue-50 dark:bg-blue-900/10 rounded-lg p-2 md:p-3 border border-blue-100 dark:border-blue-800">
                {task.description || '-'}
              </div>
            </div>

            <div>
              <label className="block text-[10px] md:text-xs font-medium text-blue-700 dark:text-blue-300 mb-0.5 md:mb-1">
                {t('tasks.fields.status') || 'Státusz'}
              </label>
              <div className="flex items-center">
                <div 
                  className="inline-flex items-center px-1.5 md:px-2 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-medium mr-2"
                  style={{ backgroundColor: statusInfo.bgColor, color: statusInfo.color }}
                >
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {statusInfo.label}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-[10px] md:text-xs font-medium text-blue-700 dark:text-blue-300 mb-0.5 md:mb-1">
                {t('tasks.fields.priority') || 'Prioritás'}
              </label>
              <div className="flex items-center">
                <div 
                  className="inline-flex items-center px-1.5 md:px-2 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-medium"
                  style={{ backgroundColor: priorityInfo.bgColor, color: priorityInfo.color }}
                >
                  <PriorityIcon className="h-3 w-3 mr-1" />
                  {priorityInfo.label}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 🟢 Időzítés és befejezés */}
        <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-2 md:p-4 border border-green-200 dark:border-green-700">
          <div className="flex items-center mb-2 md:mb-3">
            <div className="bg-green-500 rounded-full p-1 md:p-1.5 mr-1.5 md:mr-2">
              <CalendarIcon className="h-3 w-3 md:h-4 md:w-4 text-white" />
            </div>
            <h3 className="text-sm md:text-base font-semibold text-green-900 dark:text-green-300">
              {t('tasks.sections.timeCompletion') || 'Időzítés és befejezés'}
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3">
            <div>
              <label className="block text-[10px] md:text-xs font-medium text-green-700 dark:text-green-300 mb-0.5 md:mb-1">
                <CalendarIcon className="h-2.5 w-2.5 md:h-3 md:w-3 inline mr-0.5 md:mr-1" />
                {t('tasks.fields.dueDate') || 'Határidő'}
              </label>
              <div className={`text-gray-900 dark:text-gray-100 text-xs md:text-sm ${dueDateStatus() === 'overdue' ? 'text-red-600 dark:text-red-400 font-medium' : 
                dueDateStatus() === 'warning' ? 'text-yellow-600 dark:text-yellow-400 font-medium' : ''}`}>
                {formatDateTime(task.dueDate)}
                {dueDateStatus() === 'overdue' && (
                  <span className="ml-1 text-red-600 dark:text-red-400 text-[10px] md:text-xs">
                    ({t('tasks.messages.overdue') || 'Lejárt'})
                  </span>
                )}
                {dueDateStatus() === 'warning' && (
                  <span className="ml-1 text-yellow-600 dark:text-yellow-400 text-[10px] md:text-xs">
                    ({t('tasks.messages.dueSoon') || 'Hamarosan lejár'})
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-[10px] md:text-xs font-medium text-green-700 dark:text-green-300 mb-0.5 md:mb-1">
                <CalendarIcon className="h-2.5 w-2.5 md:h-3 md:w-3 inline mr-0.5 md:mr-1" />
                {t('tasks.fields.completedDate') || 'Befejezés dátuma'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 text-xs md:text-sm">{formatDateTime(task.completedDate)}</div>
            </div>

            <div>
              <label className="block text-[10px] md:text-xs font-medium text-green-700 dark:text-green-300 mb-0.5 md:mb-1">
                <ClockIcon className="h-2.5 w-2.5 md:h-3 md:w-3 inline mr-0.5 md:mr-1" />
                {t('tasks.fields.estimatedHours') || 'Becsült órák'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 text-xs md:text-sm">
                {task.estimatedHours ? `${task.estimatedHours} óra` : '-'}
              </div>
            </div>
          </div>
        </div>

        {/* 🟣 Hozzárendelés és ügyfél */}
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-2 md:p-4 border border-purple-200 dark:border-purple-700">
          <div className="flex items-center mb-2 md:mb-3">
            <div className="bg-purple-500 rounded-full p-1 md:p-1.5 mr-1.5 md:mr-2">
              <UserIcon className="h-3 w-3 md:h-4 md:w-4 text-white" />
            </div>
            <h3 className="text-sm md:text-base font-semibold text-purple-900 dark:text-purple-300">
              {t('tasks.sections.assignmentClient') || 'Hozzárendelés és ügyfél'}
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3">
            <div>
              <label className="block text-[10px] md:text-xs font-medium text-purple-700 dark:text-purple-300 mb-0.5 md:mb-1">
                <UserIcon className="h-2.5 w-2.5 md:h-3 md:w-3 inline mr-0.5 md:mr-1" />
                {t('tasks.fields.assignedTo') || 'Hozzárendelve'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 text-xs md:text-sm">{task.assignedToName || '-'}</div>
            </div>

            <div>
              <label className="block text-[10px] md:text-xs font-medium text-purple-700 dark:text-purple-300 mb-0.5 md:mb-1">
                <BuildingOffice2Icon className="h-2.5 w-2.5 md:h-3 md:w-3 inline mr-0.5 md:mr-1" />
                {t('tasks.fields.customer') || 'Ügyfél'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 text-xs md:text-sm">{task.customerName || '-'}</div>
            </div>

            <div>
              <label className="block text-[10px] md:text-xs font-medium text-purple-700 dark:text-purple-300 mb-0.5 md:mb-1">
                <BriefcaseIcon className="h-2.5 w-2.5 md:h-3 md:w-3 inline mr-0.5 md:mr-1" />
                {t('tasks.fields.project') || 'Projekt'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 text-xs md:text-sm">
                {task.projectId ? getProjectName(task.projectId) : '-'}
              </div>
            </div>
          </div>
        </div>

        {/* 🟠 Címkék és munka */}
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-lg p-4 border border-orange-200 dark:border-orange-700">
          <div className="flex items-center mb-3">
            <div className="bg-orange-500 rounded-full p-1.5 mr-2">
              <TagIcon className="h-4 w-4 text-white" />
            </div>
            <h3 className="text-base font-semibold text-orange-900 dark:text-orange-300">
              {t('tasks.sections.tagsWork') || 'Címkék és munka'}
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Manuális órák */}
            <div>
              <label className="block text-xs font-medium text-orange-700 dark:text-orange-300 mb-1">
                🖊️ {t('tasks.fields.manualHours') || 'Manuális órák'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 text-sm font-medium">
                {task.manualHours && !isNaN(task.manualHours) ? (() => {
                  const hours = Math.floor(task.manualHours);
                  const minutes = Math.round((task.manualHours - hours) * 60);
                  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
                })() : '0h'}
              </div>
            </div>

            {/* Időmérős órák (számított) */}
            <div>
              <label className="block text-xs font-medium text-orange-700 dark:text-orange-300 mb-1">
                ⏱️ Időmérős órák
              </label>
              <div className="text-gray-900 dark:text-gray-100 text-sm">
                {(() => {
                  const manualHours = task.manualHours || 0;
                  const actualHours = task.actualHours || 0;
                  const trackedHours = actualHours - manualHours;
                  const hours = Math.floor(trackedHours);
                  const minutes = Math.round((trackedHours - hours) * 60);
                  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
                })()}
              </div>
            </div>

            {/* Összes óra */}
            <div>
              <label className="block text-xs font-medium text-orange-700 dark:text-orange-300 mb-1">
                <ClockIcon className="h-3 w-3 inline mr-1" />
                {t('tasks.fields.actualHours') || 'Összes óra'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 text-sm font-bold">
                {task.actualHours && !isNaN(task.actualHours) ? (() => {
                  const hours = Math.floor(task.actualHours);
                  const minutes = Math.round((task.actualHours - hours) * 60);
                  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
                })() : '0h'}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-orange-700 dark:text-orange-300 mb-1">
                <UserIcon className="h-3 w-3 inline mr-1" />
                {t('tasks.fields.createdBy') || 'Létrehozó'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 text-sm">{task.createdByName || '-'}</div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-orange-700 dark:text-orange-300 mb-1">
                <TagIcon className="h-3 w-3 inline mr-1" />
                {t('tasks.fields.tags') || 'Címkék'}
              </label>
              <div className="text-gray-900 dark:text-gray-100">
                {task.tags && task.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {task.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="inline-flex items-center px-2 py-0.5 bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300 text-xs rounded-full"
                      >
                        <TagIcon className="h-3 w-3 mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-gray-500 dark:text-gray-400 text-sm">-</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Dokumentumok */}
        {task.documents && task.documents.length > 0 && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-800/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-700">
            <div className="flex items-center mb-3">
              <div className="bg-indigo-500 rounded-full p-1.5 mr-2">
                <DocumentTextIcon className="h-4 w-4 text-white" />
              </div>
              <h3 className="text-base font-semibold text-indigo-900 dark:text-indigo-300">
                {t('tasks.fields.documents') || 'Dokumentumok'}
              </h3>
            </div>
            
            <div className="space-y-2">
              {task.documents.map((doc, index) => (
                <div
                  key={doc.id || index}
                  className="flex items-center justify-between p-2 bg-white dark:bg-gray-700 rounded-lg border border-indigo-100 dark:border-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-sm">📄</span>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {doc.originalName || doc.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {doc.size ? `${Math.round(doc.size / 1024)} KB` : '-'} • {formatDateTime(doc.uploadedAt)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => window.open(doc.url, '_blank')}
                      className="p-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      title="Megtekintés"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <a
                      href={doc.url}
                      download={doc.originalName || doc.name}
                      className="p-1.5 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                      title="Letöltés"
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Időmérő */}
        <div className="mt-6">
          <TimeTracker 
            taskId={task.id}
            organizationId={task.organizationId}
          />
        </div>

        {/* Metaadatok */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-600 dark:text-gray-400">
            <div>
              <label className="font-medium">
                {t('common.createdAt') || 'Létrehozva'}:
              </label>
              <div>{formatDateTime(task.createdAt)}</div>
            </div>
            <div>
              <label className="font-medium">
                {t('common.updatedAt') || 'Frissítve'}:
              </label>
              <div>{formatDateTime(task.updatedAt)}</div>
            </div>
            <div>
              <label className="font-medium">
                {t('common.createdBy') || 'Létrehozó'}:
              </label>
              <div>{task.createdByName || '-'}</div>
            </div>
          </div>
        </div>

        {/* 💬 Megjegyzések */}
        {onAddComment && onDeleteComment && (
          <div className="bg-gradient-to-r from-purple-50 to-pink-100 dark:from-purple-900/20 dark:to-pink-800/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
            <div className="flex items-center mb-4">
              <div className="bg-purple-500 rounded-full p-1.5 mr-2">
                <ChatBubbleLeftIcon className="h-4 w-4 text-white" />
              </div>
              <h3 className="text-base font-semibold text-purple-900 dark:text-purple-300">
                {t('tasks.sections.comments') || 'Megjegyzések'}
              </h3>
              <span className="ml-2 px-2 py-0.5 bg-purple-200 dark:bg-purple-700 text-purple-800 dark:text-purple-200 text-xs font-medium rounded-full">
                {task.comments?.length || 0}
              </span>
            </div>
            
            <TaskComments
              task={task}
              onAddComment={onAddComment}
              onDeleteComment={onDeleteComment}
              onUpdateComment={onUpdateComment}
              onDeleteAttachment={onDeleteAttachment}
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
          <div>
            {onExportTask && (
              <button
                onClick={onExportTask}
                className="px-2 md:px-4 py-1.5 md:py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-md md:rounded-lg transition-all duration-200 font-medium flex items-center gap-1 md:gap-2 shadow-md hover:shadow-lg text-xs md:text-sm"
              >
                <ArrowDownTrayIcon className="w-3.5 h-3.5 md:w-5 md:h-5" />
                <span className="hidden sm:inline">{t('tasks.actions.export') || 'Export szövegként'}</span>
                <span className="sm:hidden">Export</span>
              </button>
            )}
          </div>
          
          <div>
            {onEdit && (
              <button
                onClick={() => {
                  onEdit(task);
                  onClose();
                }}
                className="px-3 md:px-6 py-1.5 md:py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md md:rounded-lg transition-colors font-medium flex items-center gap-1 md:gap-2 text-xs md:text-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 md:w-5 md:h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                </svg>
                <span className="hidden sm:inline">{t('common.edit') || 'Szerkesztés'}</span>
                <span className="sm:hidden">Szerk.</span>
              </button>
            )}
          </div>
        </div>

      </div>
    </Modal>
  );
};

export default TaskView;